import React, { useState, useEffect, useMemo } from 'react';
import { GiFairyWings } from 'react-icons/gi';
import { FaChevronDown, FaChevronUp, FaUserGraduate, FaSearch } from 'react-icons/fa';
import axiosInstance from './../../../api/axiosInstance';
import { Helmet } from "react-helmet-async";
import './ScholarsList.css';

const PAGE_OPTIONS = [10, 20, 30, 40, 50, 100];

const ScholarsList = () => {
  const [scholars, setScholars] = useState([]);
  
  // Filter & Search States
  const [searchQuery, setSearchQuery] = useState("");
  const [yearFilter, setYearFilter] = useState("All");
  
  // Pagination & Expand States
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [expandedRow, setExpandedRow] = useState(null);

  // Fetch scholars from the database
  useEffect(() => {
    const fetchScholars = async () => {
      try {
        const res = await axiosInstance.get('/api/scholars');
        const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        
        // Only keep Active and Non-Deleted Scholar Profiles
        setScholars(data.filter(s => !s.isDeleted && s.isActive !== false));
      } catch (error) {
        console.error('Error fetching scholars:', error);
      }
    };
    fetchScholars();
  }, []);

  // Format database data into the flattened structure
  const allScholarshipRows = useMemo(() => {
    let list = [];
    
    scholars.forEach(s => {
      // Filter out Inactive Scholarship Records
      const activeGrants = (s.scholarships || []).filter(sch => sch.isActive !== false);

      // ONLY push to list if they actually have an active scholarship granted
      if (activeGrants.length > 0) {
        activeGrants.forEach(sch => {
          const dateObj = new Date(sch.date);
          const year = !isNaN(dateObj.getTime()) ? dateObj.getFullYear().toString() : "N/A";
          
          list.push({
            id: `${s._id}-${sch._id}`, 
            year: year,
            img: s.image || null,
            name: s.name || '—', 
            fatherName: s.fatherName ? `Shri ${s.fatherName}` : '—',
            searchFather: s.fatherName || '', 
            address: s.address || '—',
            currentWork: sch.currentWork || '—', 
            date: !isNaN(dateObj.getTime()) ? dateObj.toLocaleDateString('en-IN') : '—',
            amount: sch.amount ? `₹${sch.amount.toLocaleString('en-IN')}` : '—', 
            rawAmount: sch.amount || 0,
            rawDate: !isNaN(dateObj.getTime()) ? dateObj.getTime() : 0 
          });
        });
      }
    });

    // Sort descending by date (Newest first)
    return list.sort((a, b) => b.rawDate - a.rawDate);
  }, [scholars]);

  // Dynamically generate unique years based on actual granted scholarships
  const availableYears = useMemo(() => {
    const years = [...new Set(allScholarshipRows.map(item => item.year).filter(y => y !== "N/A"))];
    years.sort((a, b) => b - a);
    return ["All", ...years];
  }, [allScholarshipRows]);

  // Apply Filters & Search
  const filteredData = useMemo(() => {
    let filtered = allScholarshipRows;

    if (yearFilter !== "All") {
      filtered = filtered.filter(item => item.year === yearFilter);
    }

    if (searchQuery.trim() !== "") {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(q) || 
        item.searchFather.toLowerCase().includes(q)
      );
    }

    return filtered;
  }, [allScholarshipRows, yearFilter, searchQuery]);

  // Calculate Total Amount based on filtered data
  const totalAmount = useMemo(() => {
    return filteredData.reduce((sum, item) => sum + item.rawAmount, 0);
  }, [filteredData]);

  // Pagination Logic
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / limit) || 1;
  const startRecord = (currentPage - 1) * limit + 1;
  const endRecord = Math.min(currentPage * limit, totalItems);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * limit;
    return filteredData.slice(start, start + limit);
  }, [filteredData, currentPage, limit]);

  const handlePageChange = (newPage) => { 
    if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage); 
  };

  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  return (
    <>
      <Helmet>
        <title>Beneficiaries List | Rajput Shiksha Kosh</title>
        <meta
          name="description"
          content="Beneficiaries list for Rajput Shiksha Kosh, showcasing students who have received scholarships and support for their education."
        />
      </Helmet>
    <section className="scholars-list-wrapper">
      <div className="scholars-list-container">

        {/* HEADER */}
        <div className="scholars-list-header">
          <h2 className="scholars-list-title">BENEFICIARIES LIST</h2>
          <div className="scholars-list-divider">
            <span className="scholars-list-divider-line"></span>
            <GiFairyWings className="scholars-list-divider-icon" />
            <span className="scholars-list-divider-line"></span>
          </div>
        </div>

        {/* TOP BAR: Search & Filters */}
        <div className="scholars-list-top-bar">
          <div className="scholars-list-search-box">
            <FaSearch className="scholars-list-icon-search" />
            <input 
              type="text" 
              placeholder="Search Name or Father Name..." 
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            />
          </div>

          <div className="scholars-list-filters">
            <select
              className="scholars-list-filter-select"
              value={yearFilter}
              onChange={(e) => { setYearFilter(e.target.value); setCurrentPage(1); }}
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year === "All" ? "Filter by Year (All)" : year}
                </option>
              ))}
            </select>

            <div className="scholars-list-page-size">
              <select 
                className="scholars-list-filter-select" 
                value={limit} 
                onChange={(e) => { setLimit(Number(e.target.value)); setCurrentPage(1); }}
              >
                {PAGE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt} /page</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* TABLE */}
        <div className="scholars-list-table-main-container">
          <div className="scholars-list-table-responsive">
            <table className="scholars-list-table">
              <thead>
                <tr>
                  <th className="scholars-list-col-sno">S.No</th>
                  <th className="scholars-list-col-date">Date</th>
                  <th className="scholars-list-col-image">Image</th>
                  <th className="scholars-list-col-name scholars-list-align-left">Beneficiary Name</th>
                  <th className="scholars-list-col-father">Father Name</th>
                  <th className="scholars-list-col-address">Address</th>
                  <th className="scholars-list-col-work">Current Work/Class</th>
                  <th className="scholars-list-col-amount">Amount</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="scholars-list-no-data" style={{textAlign: 'center', padding: '30px', color: '#64748b'}}>
                      No records match your search criteria.
                    </td>
                  </tr>
                ) : paginatedData.map((scholar, i) => (
                    <React.Fragment key={scholar.id}>
                      {/* MAIN ROW */}
                      <tr className="scholars-list-table-row">
                        <td className="scholars-list-col-sno">{startRecord + i}</td>
                        <td className="scholars-list-col-date">{scholar.date}</td>
                        <td className="scholars-list-col-image">
                          {scholar.img ? (
                            <img src={scholar.img} alt={scholar.name} className="scholars-list-profile-image" />
                          ) : (
                            <div className="scholars-list-profile-image scholars-list-fallback-img">
                              <FaUserGraduate size={32} color="#94a3b8" />
                            </div>
                          )}
                        </td>
                        <td className="scholars-list-col-name scholars-list-align-left">
                          <div className="scholars-list-name-wrapper" onClick={() => toggleRow(scholar.id)}>
                            <span className="scholars-list-font-bold">{scholar.name}</span>
                            <button className="scholars-list-expand-trigger">
                              {expandedRow === scholar.id ? <FaChevronUp /> : <FaChevronDown />}
                            </button>
                          </div>
                        </td>
                        <td className="scholars-list-col-father">{scholar.fatherName}</td>
                        <td className="scholars-list-col-address">{scholar.address}</td>
                        <td className="scholars-list-col-work">{scholar.currentWork}</td>
                        <td className="scholars-list-col-amount" style={{ color: '#27ae60', fontWeight: 'bold' }}>{scholar.amount}</td>
                      </tr>

                      {/* EXPANDED ROW */}
                      {expandedRow === scholar.id && (
                        <tr className="scholars-list-expanded-row">
                          <td colSpan="8">
                            <div className="scholars-list-expanded-content">
                              <div className="scholars-list-expanded-grid">
                                <div className="scholars-list-expanded-detail exp-father">
                                  <span className="scholars-list-expanded-label">Father Name</span>
                                  <span className="scholars-list-expanded-value">{scholar.fatherName}</span>
                                </div>
                                <div className="scholars-list-expanded-detail exp-address">
                                  <span className="scholars-list-expanded-label">Address</span>
                                  <span className="scholars-list-expanded-value">{scholar.address}</span>
                                </div>
                                <div className="scholars-list-expanded-detail exp-work">
                                  <span className="scholars-list-expanded-label">Current Work / Class</span>
                                  <span className="scholars-list-expanded-value">{scholar.currentWork}</span>
                                </div>
                                <div className="scholars-list-expanded-detail exp-date">
                                  <span className="scholars-list-expanded-label">Date Granted</span>
                                  <span className="scholars-list-expanded-value">{scholar.date}</span>
                                </div>
                                <div className="scholars-list-expanded-detail exp-amount">
                                  <span className="scholars-list-expanded-label">Disbursement Amount</span>
                                  <span className="scholars-list-expanded-value" style={{ color: '#27ae60', fontWeight: 'bold' }}>{scholar.amount}</span>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                }
              </tbody>
              {/* TABLE FOOTER FOR TOTAL */}
              <tfoot>
                <tr className="scholars-list-table-footer-row">
                  <td colSpan="8" className="scholars-list-total-cell">
                    <div className="scholars-list-total-container">
                      <span className="scholars-list-total-label">
                        {yearFilter !== "All" ? `Total Support in ${yearFilter}:` : 'Total Support Disbursed:'}
                      </span>
                      <span className="scholars-list-total-value">
                        ₹ {totalAmount.toLocaleString('en-IN')}
                      </span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* BOTTOM PAGINATION BAR */}
        <div className="scholars-list-bottom-bar">
          <div className="scholars-list-pagination-info">
            Showing {totalItems === 0 ? 0 : startRecord} to {endRecord} of {totalItems} entries
          </div>
          <div className="scholars-list-pagination-controls">
            <button 
              className="scholars-list-page-btn" 
              onClick={() => handlePageChange(currentPage - 1)} 
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <button 
              className="scholars-list-page-btn" 
              onClick={() => handlePageChange(currentPage + 1)} 
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>

      </div>
    </section>
    </>
  );
};

export default ScholarsList;